import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const links = [
  { to: "/multipolar", label: "MULTIPOLAR", testid: "home-nav-link-multipolar" },
  { to: "/openaccess", label: "OPENACCESS", testid: "home-nav-link-portfolio" },
];

export default function HomeNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="home-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,border-color] duration-500 ${
          scrolled ? "bg-[#06120E]/80 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="home-nav-logo" className="flex items-center gap-3 group">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="#D92D20" strokeWidth="1.4" />
              <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="#FF6B00" strokeWidth="1" />
              <line x1="2" y1="12" x2="22" y2="12" stroke="#FF6B00" strokeWidth="1" />
            </svg>
            <span className="font-mono text-xs tracking-[0.3em] text-[#F5F5F0] group-hover:text-white transition-colors">
              AC&nbsp;INVESTING
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8" aria-label="Navigation principale">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#A3B8B0] hover:text-[#F5F5F0] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="home-nav-menu-button"
            aria-label="Ouvrir le menu"
            className="md:hidden flex items-center justify-center w-10 h-10 border border-white/20 rounded-sm text-[#F5F5F0] hover:bg-white/5 transition-colors"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-[#06120E] flex flex-col md:hidden"
            data-testid="home-mobile-menu"
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
              <span className="font-mono text-xs tracking-[0.3em] text-[#F5F5F0]">AC&nbsp;INVESTING</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="home-nav-menu-close"
                aria-label="Fermer le menu"
                className="flex items-center justify-center w-10 h-10 border border-white/20 rounded-sm text-[#F5F5F0] hover:bg-white/5 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-10" aria-label="Navigation mobile">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`${l.testid}-mobile`}
                    className="font-mono text-lg tracking-[0.3em] uppercase text-[#F5F5F0] hover:text-[#FF6B00] transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
